import type { IngestionRun } from "@/shared/domain/ingestionRules/IngestionRun";

import { IngestionStatus, type FileRecord } from "./FileRecord";

/** The labels a file's ingestion state is shown as. */
export const IngestionLabel = {
  Ingested: "Ingested",
  Ingesting: "Ingesting…",
  PartlyIngested: "Partly ingested",
  IngestionFailed: "Ingestion failed",
  AwaitingIngestion: "Awaiting ingestion",
  NotWaitedFor: "Not waited for",
  StoredAsIs: "Stored as-is",
} as const;

/**
 * The labels a file record alone decides, which a files query can therefore
 * filter by. The rest need the file's latest run or its session.
 */
export const RECORD_INGESTION_LABELS = [
  IngestionLabel.Ingested,
  IngestionLabel.PartlyIngested,
  IngestionLabel.AwaitingIngestion,
  IngestionLabel.StoredAsIs,
] as const;

/** The one label a file's ingestion state is shown as. */
export type IngestionLabel =
  (typeof IngestionLabel)[keyof typeof IngestionLabel];

export interface IngestionLabelContext {
  /** The file's latest ingestion run; null or undefined when none is known. */
  latestRun?: Pick<IngestionRun, "status"> | null;
  /**
   * The file record was read before its latest run was seen to end, so its
   * `ingestion_status` may not show what the run did yet.
   */
  recordPredatesRunEnd?: boolean;
  /** The session stopped waiting for the file. */
  waitSkipped?: boolean;
}

/**
 * A file's ingestion state in one label. `not_ingested` alone cannot tell a
 * file waiting on ingestion from one no rule matched, which is stored as it
 * was uploaded, so `ingestable` decides between them. A known run overrides
 * both: a file being ingested, or whose last run failed or finished without
 * ingesting it, says so. A finished run is read against the record only once
 * the record was read after the run ended; until then the file shows as
 * Ingesting, not as a failure the record has not caught up with.
 */
export function ingestionLabel(
  file: Pick<FileRecord, "ingestion_status" | "ingestable">,
  {
    latestRun,
    recordPredatesRunEnd = false,
    waitSkipped = false,
  }: IngestionLabelContext = {},
): IngestionLabel {
  if (file.ingestion_status === IngestionStatus.Ingested) {
    return IngestionLabel.Ingested;
  }
  if (waitSkipped) {
    return IngestionLabel.NotWaitedFor;
  }
  if (
    latestRun?.status === "running" ||
    (latestRun?.status === "finished" && recordPredatesRunEnd)
  ) {
    return IngestionLabel.Ingesting;
  }
  if (
    latestRun?.status === "failed" ||
    (latestRun?.status === "finished" &&
      file.ingestion_status === IngestionStatus.NotIngested)
  ) {
    return IngestionLabel.IngestionFailed;
  }
  if (file.ingestion_status === IngestionStatus.PartlyIngested) {
    return IngestionLabel.PartlyIngested;
  }
  return file.ingestable === true
    ? IngestionLabel.AwaitingIngestion
    : IngestionLabel.StoredAsIs;
}
